/*
    ================================================
    Example usage of fetchWithAuth + handleApiResponse
    ================================================
    
    import { fetchBooksByTitle } from "../Api/Backend/Books";
    import { useAuth } from "../Context/AuthContext";

    const { user, refreshAccessToken, logout } = useAuth();

    fetchBooksByTitle({
        auth: { user, refreshAccessToken, logout },
        title: "Some Book Title",
        onSuccess: (data) => {
            console.log("Books:", data);
        },
        onError: (error) => {
            console.error("API Error:", error);
        },
    });
    
    ================================================
    Example API implementation:
    ================================================
    
    export const fetchBooksByTitle = async ({
        auth,
        title,
        onSuccess = null,
        onError = null,
    }) => {
        try {
        const response = await fetchWithAuth(
            `${BACKEND_API_URL}/book/keyword?title=${encodeURIComponent(title)}`,
            { method: "GET" },
            auth
        );
    
        const data = await handleApiResponse(response, "fetchBooksByTitle");
    
        if (onSuccess) onSuccess(data);
        return data;
        } catch (error) {
        if (onError) {
            onError(error);
            return;
        }
        throw error;
        }
    };
    
    ================================================
*/

export const fetchWithAuth = async (url, options = {}, auth) => {
    const { user, refreshAccessToken, logout } = auth;
    if (!user?.accessToken) {
        console.error("No JWT token found, user not authenticated.");
        return null;
    }
    
    if (!options.headers) options.headers = {};
    options.headers["Authorization"] = `Bearer ${user.accessToken}`;

    if (options.body instanceof FormData) {
        delete options.headers["Content-Type"]
    } else {
        options.headers["Content-Type"] = "application/json";
    }

    let response = await fetch(url, options);

    if (response.status === 401) {
        console.warn("JWT token expired. Refreshing...");

        const newToken = await refreshAccessToken();
        if (!newToken) {
            console.error("Failed to refresh JWT token. Logging out...");
            logout();

            return null;
        }
        options.headers["Authorization"] = `Bearer ${newToken}`;
        response = await fetch(url, options);

        if (response.status === 401) {
            console.error("Retry fetch returned 401. Logging out...");
            logout();
            return null;
        }
        console.warn("JWT token refresh successful.");
    } else if (response.status === 403) {
        console.warn("Access forbidden (403). User is authenticated but lacks permission.");
    }

    return response;
};

export const handleApiResponse = async (response, entityName) => {
    if (response.status === 204) {
        return [];
    }

    let responseBody = null;
    const contentType = response.headers.get("content-type");

    try {
        if (contentType?.includes("application/json")) {
            responseBody = await response.json();
        } else {
            responseBody = await response.text();
        }
    } catch {
        console.warn(`Warning: Unable to parse ${entityName} response.`, response);
    }

    if (!response.ok) {
        const errorMessage =
            (typeof responseBody === "string" && responseBody) ||
            responseBody?.message ||
            responseBody?.error ||
            responseBody?.errors?.join(", ") ||
            responseBody?.detail ||
            `Request failed with status: ${response.status}`;

        const errorObject = {
            status: response.status,
            message: errorMessage,
            details: responseBody ?? null
        };

        console.error(`Error in ${entityName}:`, errorObject);
        throw errorObject;
    }

    return responseBody;
};

